/**
 * Journal d'audit (docs/16 §3 et §5).
 *
 * En lecture seule, et pas seulement dans l'interface : l'API n'expose aucune
 * route d'écriture ou de suppression, et le dépôt n'a ni `save()` ni `delete()`.
 * Un administrateur ne peut pas effacer ses propres traces.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuditLog } from "@/features/admin/api/useAdmin";

import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { LoadMore } from "@/shared/ui/LoadMore";
import { Skeleton } from "@/shared/ui/Skeleton";

const ACTIONS = [
  "user.suspended",
  "user.reactivated",
  "user.role_granted",
  "user.role_revoked",
] as const;

export function AdminAuditLogPage() {
  const { t, i18n } = useTranslation("admin");
  const [action, setAction] = useState("");
  const entries = useAuditLog(action === "" ? undefined : action);
  const rows = entries.data?.pages.flatMap((page) => page.data);

  const formatDateTime = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(value),
    );

  const reasonOf = (changes: Record<string, unknown> | null | undefined) =>
    typeof changes?.["reason"] === "string" ? changes["reason"] : t("audit.noReason");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("audit.heading")}</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("audit.description")}</p>
      </div>

      <Card>
        <div className="space-y-1.5 sm:max-w-xs">
          <label htmlFor="audit-action" className="block text-sm font-medium">
            {t("audit.filterAction")}
          </label>
          <select
            id="audit-action"
            value={action}
            onChange={(event) => {
              setAction(event.target.value);
            }}
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          >
            <option value="">{t("users.any")}</option>
            {ACTIONS.map((slug) => (
              <option key={slug} value={slug}>
                {t(`actionNames.${slug}`)}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {entries.isPending && <Skeleton className="h-4 w-2/3" label={t("audit.loading")} />}
      {entries.isError && (
        <ErrorState error={entries.error} onRetry={() => void entries.refetch()} />
      )}

      {rows !== undefined &&
        (rows.length === 0 ? (
          <Card>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t("audit.empty")}</p>
          </Card>
        ) : (
          <Card className="overflow-x-auto p-0">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs text-slate-500 uppercase dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    {t("audit.columns.date")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("audit.columns.action")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("audit.columns.reason")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {rows.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300">
                      {formatDateTime(entry.created_at)}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {t(`actionNames.${entry.action}`, { defaultValue: entry.action })}
                      {entry.actor_id === null && (
                        <span className="ml-2 text-xs text-slate-500">{t("audit.system")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      {reasonOf(entry.changes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ))}

      {rows !== undefined && rows.length > 0 && (
        <LoadMore
          shown={rows.length}
          hasMore={entries.hasNextPage}
          isLoading={entries.isFetchingNextPage}
          onLoadMore={() => void entries.fetchNextPage()}
        />
      )}
    </div>
  );
}
